import React, { useState, useEffect, useCallback } from 'react';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Settings,
  Terminal,
  Globe,
  Flag,
  RotateCcw,
  Monitor,
  Server,
  Shield,
  Activity,
  Navigation,
  Lock,
  Wifi,
  AlertTriangle,
  Zap,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { logger, LogLevel, LogCategory } from '../utils/logger';
import { EnvironmentService, ENVIRONMENTS, Environment } from '../services/EnvironmentService';
import { useDataProvider } from 'react-admin';
import { CustomDataProvider, FeatureFlag } from '../types/dataProvider';

/* ============================================================
   Settings — Latio design system (derived). Developer tools + environment.
   ============================================================ */

const LOG_LEVEL_OPTIONS = [
  { value: LogLevel.ERROR, label: 'Error', description: 'Critical errors only' },
  { value: LogLevel.WARN, label: 'Warning', description: 'Errors + warnings' },
  { value: LogLevel.INFO, label: 'Info', description: 'Errors + warnings + info' },
  { value: LogLevel.DEBUG, label: 'Debug', description: 'All messages (verbose)' },
];

const CATEGORY_CONFIG: { category: LogCategory; label: string; description: string; icon: React.ElementType }[] = [
  { category: LogCategory.AUTH, label: 'Authentication', description: 'Login, logout, token refresh', icon: Lock },
  { category: LogCategory.API, label: 'API Calls', description: 'Data fetching, CRUD operations', icon: Server },
  { category: LogCategory.TOKEN, label: 'Token Management', description: 'JWT tokens, storage, expiry', icon: Shield },
  { category: LogCategory.UI, label: 'UI Events', description: 'Component lifecycle, rendering', icon: Monitor },
  { category: LogCategory.NAVIGATION, label: 'Navigation', description: 'Route changes, redirects', icon: Navigation },
  { category: LogCategory.WEBSOCKET, label: 'WebSocket', description: 'Real-time connections', icon: Wifi },
  { category: LogCategory.ERROR, label: 'Error Tracking', description: 'Error boundaries, unhandled errors', icon: AlertTriangle },
  { category: LogCategory.PERFORMANCE, label: 'Performance', description: 'Timing, load metrics', icon: Zap },
  { category: LogCategory.SECURITY, label: 'Security', description: 'Access control, validation', icon: Shield },
];

function useLoggerConfig() {
  const [config, setConfig] = useState(logger.getCurrentConfig());
  const refresh = useCallback(() => setConfig(logger.getCurrentConfig()), []);
  useEffect(() => logger.subscribe(refresh), [refresh]);
  return config;
}

function useFeatureFlags(envName: string) {
  const dataProvider = useDataProvider<CustomDataProvider>();
  const [flags, setFlags] = useState<FeatureFlag[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setFlags([]);
    setLoading(true);
    setError(null);
    try {
      const { data } = await dataProvider.getFeatureFlags();
      setFlags(data);
    } catch (e: any) {
      setError(e.message || 'Failed to load feature flags');
    } finally {
      setLoading(false);
    }
    // envName is intentionally unused in the body: it exists so a change in the
    // selected environment gives `refresh` a new identity, which re-triggers the
    // effect below and re-fetches flags for the newly selected environment.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataProvider, envName]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const toggle = useCallback(
    async (key: string, enabled: boolean) => {
      const previousEnabled = flags.find((f) => f.key === key)?.enabled;
      setError(null);
      setFlags((prev) => prev.map((f) => (f.key === key ? { ...f, enabled } : f)));
      try {
        await dataProvider.updateFeatureFlag({ key, enabled });
      } catch (e: any) {
        setError(e.message || 'Failed to update feature flag');
        setFlags((prev) =>
          prev.map((f) => (f.key === key ? { ...f, enabled: previousEnabled ?? f.enabled } : f)),
        );
      }
    },
    [dataProvider, flags],
  );

  return { flags, loading, error, toggle };
}

function SectionCard({
  title,
  icon: Icon,
  description,
  action,
  children,
}: {
  title: string;
  icon: React.ElementType;
  description?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-card border border-hairline bg-background p-5">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h3 className="flex items-center gap-2 text-[15px] font-bold text-ink">
            <Icon className="size-4 text-ink-2" />
            {title}
          </h3>
          {description && <p className="mt-0.5 text-[13px] text-ink-2">{description}</p>}
        </div>
        {action}
      </div>
      <Separator className="mb-4" />
      {children}
    </div>
  );
}

function PillBtn({ onClick, disabled, children }: { onClick: () => void; disabled?: boolean; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="inline-flex h-8 items-center gap-1.5 rounded-pill border border-hairline px-3 text-[13px] font-semibold text-ink-2 hover:bg-surface-2 hover:text-ink disabled:opacity-50"
    >
      {children}
    </button>
  );
}

function InfoTile({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="rounded-input border border-hairline bg-surface-2 p-3">
      <div className="text-[11.5px] font-semibold uppercase tracking-[0.06em] text-ink-3">{label}</div>
      <div className="mt-0.5 text-sm font-medium text-ink">{value}</div>
    </div>
  );
}

export function SettingsPage() {
  const logConfig = useLoggerConfig();
  const [currentEnv, setCurrentEnv] = useState<Environment>(EnvironmentService.getCurrentEnvironment());
  const { flags, loading: flagsLoading, error: flagsError, toggle: toggleFlag } = useFeatureFlags(currentEnv.name);

  const allEnabled = logConfig.enabledCategories.length === Object.values(LogCategory).length;
  const noneEnabled = logConfig.enabledCategories.length === 0;
  const currentLevelOption = LOG_LEVEL_OPTIONS.find((o) => o.value === logConfig.logLevel);

  return (
    <div className="space-y-6 p-6" style={{ maxWidth: 920 }}>
      <div>
        <h1 className="flex items-center gap-2 text-[24px] font-bold leading-none text-ink" style={{ letterSpacing: '-0.02em' }}>
          <Settings className="size-6 text-ink-2" />
          Settings
        </h1>
        <p className="mt-1 text-sm text-ink-2">Manage application preferences and developer tools</p>
      </div>

      {/* Console logging */}
      <SectionCard
        title="Console logging"
        icon={Terminal}
        description="Control the verbosity and categories of console output for debugging"
        action={
          <PillBtn onClick={() => logger.resetToDefaults()}>
            <RotateCcw className="size-3.5" />
            Reset to defaults
          </PillBtn>
        }
      >
        <div className="mb-6">
          <Label className="mb-2 block text-sm font-medium text-ink">Log level</Label>
          <div className="flex items-center gap-3">
            <Select value={String(logConfig.logLevel)} onValueChange={(v) => logger.setLogLevel(Number(v) as LogLevel)}>
              <SelectTrigger className="w-[200px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LOG_LEVEL_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={String(opt.value)}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {currentLevelOption && <span className="text-sm text-ink-2">{currentLevelOption.description}</span>}
          </div>
        </div>

        <div>
          <div className="mb-3 flex items-center justify-between">
            <Label className="text-sm font-medium text-ink">Log categories</Label>
            <div className="flex gap-2">
              <PillBtn onClick={() => logger.enableAllCategories()} disabled={allEnabled}>Enable all</PillBtn>
              <PillBtn onClick={() => logger.disableAllCategories()} disabled={noneEnabled}>Disable all</PillBtn>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-2">
            {CATEGORY_CONFIG.map(({ category, label, description, icon: Icon }) => {
              const isEnabled = logConfig.enabledCategories.includes(category);
              return (
                <div
                  key={category}
                  className={cn(
                    'flex items-center justify-between rounded-input border px-4 py-3',
                    isEnabled ? 'border-hairline bg-background' : 'border-hairline bg-surface-2',
                  )}
                >
                  <div className="flex items-center gap-3">
                    <Icon className={cn('size-4', isEnabled ? 'text-ink-2' : 'text-ink-3')} />
                    <div>
                      <div className="flex items-center gap-2">
                        <span className={cn('text-sm font-medium', isEnabled ? 'text-ink' : 'text-ink-3')}>{label}</span>
                        <Badge variant="outline" className="px-1.5 py-0 text-[10px]">{category}</Badge>
                      </div>
                      <span className="text-[12px] text-ink-3">{description}</span>
                    </div>
                  </div>
                  <Switch checked={isEnabled} onCheckedChange={() => logger.toggleCategory(category)} />
                </div>
              );
            })}
          </div>
        </div>
      </SectionCard>

      {/* Feature Flags */}
      <SectionCard
        title="Feature flags"
        icon={Flag}
        description={`Toggle product features across backend, mobile, and admin (${currentEnv.label})`}
      >
        {flagsLoading && <p className="text-sm text-ink-2">Loading...</p>}
        {flagsError && <p className="text-sm text-red-600">{flagsError}</p>}
        {!flagsLoading && flags.length === 0 && !flagsError && (
          <p className="text-sm text-ink-2">No feature flags configured.</p>
        )}
        <div className="grid grid-cols-1 gap-2">
          {flags.map((flag) => (
            <div
              key={flag.key}
              className={cn(
                'flex items-center justify-between rounded-input border px-4 py-3',
                flag.enabled ? 'border-hairline bg-background' : 'border-hairline bg-surface-2',
              )}
            >
              <div>
                <span
                  className={cn(
                    'text-sm font-medium',
                    flag.enabled ? 'text-ink' : 'text-ink-3',
                  )}
                >
                  {flag.key}
                </span>
                {flag.description && (
                  <div className="text-[12px] text-ink-3">{flag.description}</div>
                )}
              </div>
              <Switch
                checked={flag.enabled}
                onCheckedChange={(checked) => toggleFlag(flag.key, checked)}
              />
            </div>
          ))}
        </div>
      </SectionCard>

      {/* Environment */}
      <SectionCard
        title="Environment"
        icon={Globe}
        description="Switch between API environments"
        action={
          <PillBtn
            onClick={() => {
              EnvironmentService.resetToDefault();
              setCurrentEnv(EnvironmentService.getCurrentEnvironment());
            }}
          >
            <RotateCcw className="size-3.5" />
            Reset
          </PillBtn>
        }
      >
        <div className="mb-4">
          <Label className="mb-2 block text-sm font-medium text-ink">API environment</Label>
          <Select
            value={currentEnv.name}
            onValueChange={(name) => {
              EnvironmentService.setEnvironment(name);
              setCurrentEnv(EnvironmentService.getCurrentEnvironment());
            }}
          >
            <SelectTrigger className="w-[280px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ENVIRONMENTS.map((env) => (
                <SelectItem key={env.name} value={env.name}>
                  {env.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2 rounded-input border border-hairline bg-surface-2 p-4">
          {[
            ['API URL', currentEnv.apiUrl],
            ['Auth', currentEnv.authEndpoint],
            ...(currentEnv.websocketUrl ? [['WebSocket', currentEnv.websocketUrl]] : []),
          ].map(([label, value]) => (
            <div key={label} className="flex items-center gap-2">
              <span className="min-w-20 text-[12px] font-semibold text-ink-2">{label}</span>
              <code className="rounded border border-hairline bg-background px-2 py-1 text-xs text-ink">{value}</code>
            </div>
          ))}
        </div>
      </SectionCard>

      {/* App info */}
      <SectionCard title="Application info" icon={Activity}>
        <div className="grid grid-cols-2 gap-4">
          <InfoTile label="Environment" value={process.env.NODE_ENV} />
          <InfoTile label="API target" value={currentEnv.label} />
          <InfoTile label="Log level" value={currentLevelOption?.label ?? 'Unknown'} />
          <InfoTile label="Active categories" value={`${logConfig.enabledCategories.length} / ${Object.values(LogCategory).length}`} />
        </div>
      </SectionCard>
    </div>
  );
}
